import React from "react";
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

const STATUS_COLORS = {
  "New":         "#00ff90",
  "In Progress": "#60a5fa",
  "Waiting":     "#fbbf24",
  "Resolved":    "#34d399",
  "Closed":      "rgba(255,255,255,0.2)",
};

export default function TicketsStatus({ loading, data, kpis }) {
  const byStatus  = data?.byStatus  || [];
  const weekTrend = data?.weekTrend || []; // [{week, count}]
  const byCategory = data?.byCategory || [];

  const donutData = byStatus.map(r => ({
    name: r.status, value: r.count,
    color: STATUS_COLORS[r.status] || "#ffffff22",
  }));

  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Tickets Status</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Tickets</span>
        {kpis?.ticketsOpen != null && (
          <span className="text-sm font-bold text-[#00ff90]">{kpis.ticketsOpen} open</span>
        )}
      </div>
      <div className="p-5 space-y-5">

        {/* Stat chips */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Open",           value: kpis?.ticketsOpen,      color: "#00ff90" },
            { label: "SLA Risk",       value: kpis?.ticketsSlaRisk,   color: "#f87171" },
            { label: "Avg Resolution", value: kpis?.avgResolutionHrs != null ? `${kpis.avgResolutionHrs}h` : "—", color: "#60a5fa" },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
              <p className="text-sm font-bold" style={{ color }}>{value ?? "—"}</p>
              <p className="text-xs text-white/30 mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Status donut */}
        {donutData.length > 0 && (
          <div className="flex items-center gap-4">
            <ResponsiveContainer width={100} height={100}>
              <PieChart>
                <Pie data={donutData} cx="50%" cy="50%" innerRadius={26} outerRadius={44} paddingAngle={2} dataKey="value">
                  {donutData.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.85} />)}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v} tickets`, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-1.5">
              {donutData.map(d => (
                <div key={d.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                    <span className="text-xs text-white/50">{d.name}</span>
                  </div>
                  <span className="text-xs font-medium" style={{ color: d.color }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Volume trend */}
        {weekTrend.length > 1 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Volume Trend</p>
            <ResponsiveContainer width="100%" height={80}>
              <LineChart data={weekTrend} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="week" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} formatter={v => [`${v} tickets`, "Volume"]} />
                <Line type="monotone" dataKey="count" stroke="#00ff90" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* By category */}
        {byCategory.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">By Category</p>
            <div className="space-y-1.5">
              {byCategory.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-white/50">{c.category}</span>
                  <span className="text-white/70 font-medium">{c.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}